import { zodResolver } from '@hookform/resolvers/zod';
import { isApiError } from '@saay/api-client';
import {
  businessDayBounds,
  formatTimestamp,
  statusLabel,
  statusValues,
  toPersianDigits,
  type LocalDate,
} from '@saay/formatting';
import { useQueryClient } from '@tanstack/react-query';
import { useCommand } from '@saay/web-app';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import type { BuyerWalletMultiplier, BuyerWalletMultiplierOverrideCommand } from '../api.js';
import {
  AmountFilterPill,
  DateFilterPill,
  FilterPill,
  FilterPillBar,
} from '../components/filter-pills.js';
import { normalizeDecimalInput, useSubmitBlocked } from '../components/form.js';
import { QueryState } from '../components/guard.js';
import {
  applyFieldErrors,
  Banner,
  Button,
  CommandFeedback,
  DetailGrid,
  EmptyState,
  LoadMore,
  Masked,
  Money,
  Section,
  Skeleton,
  Switch,
  TextArea,
  TextField,
} from '../kit.js';
import { canOverrideWalletMultiplier } from '../permissions.js';
import {
  setupKeys,
  useAdminBuyerMoneyTimeline,
  useAdminBuyerWallet,
  type MoneyTimelineFilters,
} from '../queries.js';
import type { AdminSelf } from '@saay/api-client';
import {
  TimelineDetail,
  TimelineTable,
  timelineView,
  type TimelineView,
} from './wallet-timeline.js';

const overrideSchema = z
  .object({
    multiplier: z.string(),
    reason: z.string().trim().min(1, 'دلیل را بنویسید.').max(500, 'دلیل بلندتر از حد مجاز است.'),
    remove: z.boolean(),
  })
  .superRefine((value, context) => {
    if (value.remove) return;
    if (!/^\d{1,3}\.\d{2}$/.test(value.multiplier)) {
      context.addIssue({
        code: 'custom',
        message: 'ضریب را مثل ۶ یا ۶٫۵ وارد کنید (حداکثر دو رقم اعشار).',
        path: ['multiplier'],
      });
      return;
    }
    const numeric = Number(value.multiplier);
    if (numeric < 1 || numeric > 100) {
      context.addIssue({
        code: 'custom',
        message: 'ضریب باید بین ۱ و ۱۰۰ باشد.',
        path: ['multiplier'],
      });
    }
  });

type OverrideFormValues = z.infer<typeof overrideSchema>;

function dateBound(value: string, edge: 'start' | 'end'): string | undefined {
  return value === '' ? undefined : businessDayBounds(value)[edge];
}

function OverrideForm({
  buyerId,
  version,
}: {
  readonly buyerId: string;
  readonly version: number;
}) {
  const queryClient = useQueryClient();
  const offline = useSubmitBlocked();
  const [conflict, setConflict] = useState<string | null>(null);
  const [blocked, setBlocked] = useState<string | null>(null);
  const form = useForm<OverrideFormValues>({
    defaultValues: { multiplier: '', reason: '', remove: false },
    resolver: zodResolver(overrideSchema),
  });
  const remove = form.watch('remove');
  const command = useCommand<BuyerWalletMultiplierOverrideCommand, BuyerWalletMultiplier>(
    'buyer.wallet-multiplier',
    (client, body, intent) =>
      client.post(`/admin/buyers/${encodeURIComponent(buyerId)}/wallet-multiplier-overrides`, {
        body,
        intent,
      }),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: setupKeys.buyerWallet(buyerId) });
        await queryClient.invalidateQueries({ queryKey: setupKeys.buyerTimeline(buyerId) });
        form.reset({ multiplier: '', reason: '', remove: false });
        setConflict(null);
        setBlocked(null);
      },
    },
  );

  async function onSubmit(values: OverrideFormValues) {
    setConflict(null);
    setBlocked(null);
    const outcome = await command.submit({
      expectedVersion: version,
      multiplier: values.remove ? null : values.multiplier,
      reason: values.reason,
    });
    if (outcome.status !== 'error' || !isApiError(outcome.error)) return;
    const error = outcome.error;
    applyFieldErrors(error, ['multiplier', 'reason'], form.setError);
    const versionError = error.fieldErrors.find((item) => item.field === 'expectedVersion');
    if (versionError !== undefined) setConflict(versionError.messageFa);
    if (error.code === 'RESOURCE_VERSION_CONFLICT') {
      setConflict(error.messageFa);
      void queryClient.invalidateQueries({ queryKey: setupKeys.buyerWallet(buyerId) });
    }
    if (error.code === 'WALLET_MULTIPLIER_OVERRIDE_UNCHANGED') {
      form.setError('multiplier', { message: error.messageFa, type: 'server' });
    }
    if (error.code === 'WALLET_MULTIPLIER_OVERRIDE_NOT_ALLOWED') {
      setBlocked(error.messageFa);
    }
  }

  const feedback =
    command.state.status === 'error' &&
    isApiError(command.state.error) &&
    (command.state.error.code === 'RESOURCE_VERSION_CONFLICT' ||
      command.state.error.code === 'WALLET_MULTIPLIER_OVERRIDE_UNCHANGED' ||
      command.state.error.code === 'WALLET_MULTIPLIER_OVERRIDE_NOT_ALLOWED' ||
      command.state.error.code === 'REQUEST_INVALID')
      ? { ...command.state, status: 'idle' as const }
      : command.state;

  return (
    <form className="admin-form" noValidate onSubmit={form.handleSubmit(onSubmit)}>
      <h3>ضریب اختصاصی</h3>
      {conflict === null ? null : <p role="alert">{conflict}</p>}
      {blocked === null ? null : <Banner tone="danger">{blocked}</Banner>}
      <TextField
        disabled={remove}
        error={form.formState.errors.multiplier?.message}
        hint="از ۱ تا ۱۰۰؛ مثل ۶ یا ۶٫۵"
        label="ضریب اعتبار"
        ltr
        {...form.register('multiplier', {
          setValueAs: (value: unknown) => normalizeDecimalInput(value, 2),
        })}
      />
      <Controller
        control={form.control}
        name="remove"
        render={({ field }) => (
          <Switch
            checked={field.value}
            label="برگشت به ضریب سراسری"
            onCheckedChange={field.onChange}
          />
        )}
      />
      <TextArea
        error={form.formState.errors.reason?.message}
        label="دلیل"
        {...form.register('reason')}
      />
      <CommandFeedback onCheckStatus={() => void command.checkStatus()} state={feedback} />
      <Button disabled={offline.blocked} loading={command.state.status === 'pending'} type="submit">
        ثبت ضریب
      </Button>
    </form>
  );
}

function TimelineSection({ buyerId }: { readonly buyerId: string }) {
  const [kind, setKind] = useState('');
  const [status, setStatus] = useState('');
  const [paymentType, setPaymentType] = useState('');
  const [paymentSource, setPaymentSource] = useState('');
  const [from, setFrom] = useState<LocalDate>('');
  const [to, setTo] = useState<LocalDate>('');
  const [amountFrom, setAmountFrom] = useState<number | null>(null);
  const [amountTo, setAmountTo] = useState<number | null>(null);
  const [open, setOpen] = useState<TimelineView | null>(null);
  const occurredFrom = dateBound(from, 'start');
  const occurredTo = dateBound(to, 'end');
  const filters: MoneyTimelineFilters = {
    ...(kind === '' ? {} : { kind }),
    ...(status === '' ? {} : { status }),
    ...(paymentSource === '' ? {} : { paymentSource }),
    ...(paymentType === 'INSTALLMENT' || paymentType === 'WALLET_CASH' ? { paymentType } : {}),
    ...(occurredFrom === undefined ? {} : { occurredFrom }),
    ...(occurredTo === undefined ? {} : { occurredTo }),
    ...(amountFrom === null ? {} : { amountFrom }),
    ...(amountTo === null ? {} : { amountTo }),
    sort: '-occurredAt',
  };
  const list = useAdminBuyerMoneyTimeline(buyerId, filters);
  const rows = (list.data?.pages.flatMap((page) => page.items) ?? []).map(timelineView);
  return (
    <Section id="buyer-money-timeline" title="گردش پول">
      <FilterPillBar
        activeFilterCount={
          [kind, status, paymentType, paymentSource].filter(Boolean).length +
          (from === '' && to === '' ? 0 : 1) +
          (amountFrom === null && amountTo === null ? 0 : 1)
        }
        label="فیلتر گردش پول"
        onClearAll={() => {
          setKind('');
          setStatus('');
          setPaymentType('');
          setPaymentSource('');
          setFrom('');
          setTo('');
          setAmountFrom(null);
          setAmountTo(null);
        }}
        sort={
          <FilterPill
            inactiveLabel="جدیدترین"
            label="مرتب‌سازی"
            onChange={() => undefined}
            options={[{ label: 'جدیدترین', value: '' }]}
            value=""
          />
        }
      >
        <FilterPill
          emptyLabel="همه نوع‌ها"
          label="نوع"
          onChange={setKind}
          options={statusValues('moneyTimelineKind').map((value) => ({
            label: statusLabel('moneyTimelineKind', value),
            value,
          }))}
          value={kind}
        />
        <FilterPill
          emptyLabel="همه وضعیت‌ها"
          label="وضعیت"
          onChange={setStatus}
          options={[
            { label: 'ثبت‌شده', value: 'POSTED' },
            { label: 'موفق', value: 'SUCCEEDED' },
            { label: 'واریز به کیف پول', value: 'CREDITED_TO_WALLET' },
          ]}
          value={status}
        />
        <FilterPill
          emptyLabel="همه نوع‌های خرید"
          label="نوع خرید"
          onChange={setPaymentType}
          options={statusValues('salePaymentType').map((value) => ({
            label: statusLabel('salePaymentType', value),
            value,
          }))}
          value={paymentType}
        />
        <FilterPill
          emptyLabel="همه منابع پرداخت"
          label="منبع پرداخت"
          onChange={setPaymentSource}
          options={statusValues('moneyTimelinePaymentSource').map((value) => ({
            label: statusLabel('moneyTimelinePaymentSource', value),
            value,
          }))}
          value={paymentSource}
        />
        <DateFilterPill
          from={from}
          namePrefix="timeline"
          onApply={(nextFrom, nextTo) => {
            setFrom(nextFrom);
            setTo(nextTo);
          }}
          to={to}
        />
        <AmountFilterPill
          from={amountFrom}
          onApply={(nextFrom, nextTo) => {
            setAmountFrom(nextFrom);
            setAmountTo(nextTo);
          }}
          to={amountTo}
        />
      </FilterPillBar>
      <QueryState
        empty={<EmptyState title="گردش پولی برای این خریدار نیست" />}
        error={list.error}
        isEmpty={rows.length === 0}
        onRetry={() => void list.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری گردش پول…" />}
        status={list.status}
      >
        <TimelineTable caption="گردش پول خریدار" onOpen={setOpen} rows={rows} showBuyer={false} />
        <LoadMore
          hasMore={list.hasNextPage}
          loading={list.isFetchingNextPage}
          onLoadMore={() => void list.fetchNextPage()}
        />
      </QueryState>
      <TimelineDetail onClose={() => setOpen(null)} row={open} />
    </Section>
  );
}

/** Buyer wallet balances, the effective k, and the money timeline (WL15, WL29, WL30). */
export function BuyerWalletPanel({
  buyerId,
  self,
}: {
  readonly buyerId: string;
  readonly self: AdminSelf;
}) {
  const wallet = useAdminBuyerWallet(buyerId);
  const data = wallet.data;
  const multiplier = data?.walletMultiplier;
  return (
    <div className="admin-sections">
      <Section id="buyer-wallet" title="کیف پول">
        <QueryState
          error={wallet.error}
          onRetry={() => void wallet.refetch()}
          skeleton={<Skeleton label="در حال بارگذاری کیف پول…" />}
          status={wallet.status}
        >
          {data === undefined || multiplier === undefined ? null : (
            <>
              {data.id === null ? <p>این خریدار هنوز کیف پول ندارد</p> : null}
              <h3>حساب برداشت</h3>
              {data.payoutAccount === null ? (
                <p>حساب برداشتی ثبت نشده است.</p>
              ) : (
                <DetailGrid
                  items={[
                    { key: 'holder', label: 'صاحب حساب', value: data.payoutAccount.holderName },
                    {
                      key: 'sheba',
                      label: 'شبا',
                      value: <Masked kind="sheba" value={data.payoutAccount.shebaMasked} />,
                    },
                    {
                      key: 'registered',
                      label: 'زمان ثبت حساب',
                      value: formatTimestamp(data.payoutAccount.registeredAt),
                    },
                  ]}
                />
              )}
              <DetailGrid
                items={[
                  {
                    key: 'free',
                    label: 'موجودی آزاد',
                    value: <Money rials={data.freeBalanceIrr} />,
                  },
                  {
                    key: 'held',
                    label: 'در انتظار تأیید خرید',
                    value: <Money rials={data.heldBalanceIrr} />,
                  },
                  {
                    key: 'frozen',
                    label: 'مبلغ بلوکه‌شده',
                    value: <Money rials={data.frozenBalanceIrr} />,
                  },
                  {
                    key: 'pending',
                    label: 'در حال برداشت',
                    value: <Money rials={data.withdrawalPendingIrr} />,
                  },
                  {
                    key: 'total',
                    label: 'موجودی کل',
                    value: <Money rials={data.totalBalanceIrr} />,
                  },
                  {
                    key: 'credit',
                    label: 'اعتبار از کیف پول',
                    value: <Money rials={data.walletCreditAvailableIrr} />,
                  },
                  {
                    key: 'k',
                    label: 'ضریب مؤثر',
                    value:
                      multiplier.effective === null ? '—' : toPersianDigits(multiplier.effective),
                  },
                  {
                    key: 'source',
                    label: 'منبع ضریب',
                    value: statusLabel('multiplierSource', multiplier.source),
                  },
                  {
                    key: 'global',
                    label: 'ضریب سراسری',
                    value:
                      multiplier.globalMultiplier === null
                        ? '—'
                        : toPersianDigits(multiplier.globalMultiplier),
                  },
                  ...(multiplier.override === null
                    ? []
                    : [
                        {
                          key: 'override-reason',
                          label: 'دلیل ضریب اختصاصی',
                          value: multiplier.override.reason,
                        },
                        {
                          key: 'override-by',
                          label: 'ثبت‌کننده',
                          value: multiplier.override.setBy.displayNameFa ?? '—',
                        },
                        {
                          key: 'override-at',
                          label: 'زمان ثبت',
                          value: formatTimestamp(multiplier.override.setAt),
                        },
                      ]),
                ]}
              />
              {canOverrideWalletMultiplier(self) ? (
                <OverrideForm
                  buyerId={buyerId}
                  key={multiplier.version}
                  version={multiplier.version}
                />
              ) : null}
            </>
          )}
        </QueryState>
      </Section>
      <TimelineSection buyerId={buyerId} />
    </div>
  );
}
